/**
 * Operations phases — the single source of truth for the lifecycle vocabulary
 * and its presentation, imprinted from the Case Portal (engineering/portal/
 * portal.html). One entry per Operations book; everything that needs to name a
 * phase (the summary cards, page headers, the status pills, the Surface's
 * mixed-phase cards) reads from here so the language stays consistent and a
 * rename happens in one place.
 *
 * Naming (2026-05-25): the books were renamed from the cheeky originals back to
 * the portal's status vocabulary — the names firms and operators actually use.
 * Only the human-facing `label` moved then. On 2026-10-16 (#1142) the route
 * `key`, `path`, API path, `card.kind`, testids and file names followed the
 * label, so each book is named one way everywhere: under-review, accepted,
 * placed, settled. The four old URLs (/operations/whirlpool, /fishing,
 * /holding-pen, /home-stretch) redirect to these in router.tsx.
 *   Closed / Dropped / Rejected, The Surface, New Leads → unchanged
 *
 * Colour (2026-06-30, Aesthetic Parity — ADR-0132 supersedes the Q-VALENCE
 * cool-arc scheme): PHASE IS NEUTRAL. Lifecycle position is a fact — it makes no
 * call to action — so it earns no hue. Identity is carried by the WORD (the
 * label) and the PIPELINE POSITION, rendered in the brand's navy ink. Colour on
 * these surfaces now comes only from a *condition* on a matter (severity =
 * priority, feedback = state), layered over the neutral phase — never from phase
 * identity. The valence glyphs (✓/↓/⃠ on Closed/Dropped/Rejected) are removed: a
 * terminal phase demands nothing.
 *
 * `accent` is the neutral phase ink — navy `hsl(var(--foreground))`, theme-aware
 * — used for the book-heading tick (the one rule that remains).
 * `badge` is the neutral status-chip treatment — a quiet muted fill with a navy
 * uppercase label (Q-CHIP: a tight instrument label; the word is the signal). It
 * is deliberately recessive: on a mixed-phase surface the eye should go to the
 * *condition* (severity / feedback), not the phase identity. Both are one shared
 * value (NEUTRAL_ACCENT / NEUTRAL_BADGE) so "phase = neutral" lives in one place.
 */

/** The neutral phase treatment (ADR-0132), shared by every phase. Phase carries no
 *  per-phase hue; the word + position carry identity, and colour comes from a
 *  *condition* layered over it — so the chip is a quiet muted fill that recedes and
 *  lets the condition signal speak. `accent` is navy ink for the book-heading tick. */
const NEUTRAL_ACCENT = "hsl(var(--foreground))";
const NEUTRAL_BADGE = "border-transparent bg-muted text-foreground";

/** Stable route/key identity for each Operations surface — paths, testids and
 *  `card.kind` joins. Since #1142 each key is its label's slug. */
export type OperationsPhaseKey =
  | "surface"
  | "new-leads"
  | "under-review"
  | "accepted"
  | "placed"
  | "settled"
  | "closed"
  | "dropped"
  | "rejected";

export interface OperationsPhase {
  /** Tab key — the router's identity for the surface. */
  key: OperationsPhaseKey;
  /** Human-facing label — the portal's status vocabulary. */
  label: string;
  /** Canonical route path. */
  path: string;
  /** Portal-style one-liner under the count on the summary card. */
  subtitle: string;
  /** Neutral phase ink (NEUTRAL_ACCENT) — the book-heading tick. */
  accent: string;
  /** Neutral status-chip classes (NEUTRAL_BADGE) — quiet muted fill + navy label. */
  badge: string;
}

/**
 * The phases in pipeline order — the order they appear in the summary-card row.
 * The Surface and New Leads lead (hub + intake), then the lifecycle proper, then
 * the terminal books.
 */
export const OPERATIONS_PHASES: readonly OperationsPhase[] = [
  {
    key: "surface",
    label: "The Surface",
    path: "/operations/surface",
    subtitle: "What needs us now",
    accent: NEUTRAL_ACCENT,
    badge: NEUTRAL_BADGE,
  },
  {
    key: "new-leads",
    label: "New Leads",
    path: "/operations/intake-verification",
    subtitle: "Fresh intake to verify",
    accent: NEUTRAL_ACCENT,
    badge: NEUTRAL_BADGE,
  },
  {
    key: "under-review",
    label: "Under Review",
    path: "/operations/under-review",
    subtitle: "Routing — awaiting firm acceptance",
    accent: NEUTRAL_ACCENT,
    badge: NEUTRAL_BADGE,
  },
  {
    key: "accepted",
    label: "Accepted",
    path: "/operations/accepted",
    subtitle: "Firm signing client — awaiting FSA",
    accent: NEUTRAL_ACCENT,
    badge: NEUTRAL_BADGE,
  },
  {
    key: "placed",
    label: "Placed",
    path: "/operations/placed",
    subtitle: "Client signed — active",
    accent: NEUTRAL_ACCENT,
    badge: NEUTRAL_BADGE,
  },
  {
    key: "settled",
    label: "Settled",
    path: "/operations/settled",
    subtitle: "Awaiting disbursement",
    accent: NEUTRAL_ACCENT,
    badge: NEUTRAL_BADGE,
  },
  {
    key: "closed",
    label: "Closed",
    path: "/operations/closed",
    subtitle: "Case closed",
    accent: NEUTRAL_ACCENT,
    badge: NEUTRAL_BADGE,
  },
  {
    key: "dropped",
    label: "Dropped",
    path: "/operations/dropped",
    subtitle: "Firm dropped the case",
    accent: NEUTRAL_ACCENT,
    badge: NEUTRAL_BADGE,
  },
  {
    key: "rejected",
    label: "Rejected",
    path: "/operations/rejected",
    subtitle: "No firm took the lead",
    accent: NEUTRAL_ACCENT,
    badge: NEUTRAL_BADGE,
  },
] as const;

/** Lookup by route key (the surface's stable identity). */
export const PHASE_BY_KEY: Record<string, OperationsPhase> = Object.fromEntries(
  OPERATIONS_PHASES.map((p) => [p.key, p]),
);

/**
 * Map a Surface `card.kind` to its phase, so a mixed-phase list (The Surface)
 * can name each card by where its matter currently lives. The kinds come from
 * `SurfaceCard` in @command-center/shared; several collapse to one book.
 */
export function phaseForCardKind(kind: string): OperationsPhase | undefined {
  switch (kind) {
    case "intake":
    case "lead":
      return PHASE_BY_KEY["new-leads"];
    case "under-review-lead":
    case "under-review-misc":
      return PHASE_BY_KEY["under-review"];
    case "accepted":
      return PHASE_BY_KEY["accepted"];
    case "placed":
      return PHASE_BY_KEY["placed"];
    case "settled":
      return PHASE_BY_KEY["settled"];
    case "dropped":
      return PHASE_BY_KEY["dropped"];
    case "closed":
      return PHASE_BY_KEY["closed"];
    case "rejected":
      return PHASE_BY_KEY["rejected"];
    default:
      return undefined;
  }
}
